// apps/client/src/pages/assets/Assets.tsx
import {
  ActivityAllocation,
  ProjectAsset,
  ResearchActivity,
  ResearchActivityType,
  UserRole,
  XeroSyncAssetsResponse,
} from '@avanzu/types';
import {
  CheckCircle,
  DeleteSweep,
  DevicesOutlined,
  PlaylistAddCheck,
  ScienceOutlined,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import HelpComponent from '../../components/HelpComponent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useDebounce } from '../../hooks/useDebounce';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services/apiClient';
import { convertAllocationsToObject } from '../../utils/convertAllocationsToObject';
import AssetTableRow from './components/AssetTableRow';
import XeroSyncAssets from './components/XeroSyncAssets';

export const AssetsPageRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN, UserRole.MEMBER];

// Sort functions
const sortAssetsByNumber = (a: ProjectAsset, b: ProjectAsset) => {
  // Sort by asset number descending (highest first)
  const aNum = a.assetNumber || '';
  const bNum = b.assetNumber || '';

  // If both have numbers, compare numerically (descending)
  if (aNum && bNum) {
    return bNum.localeCompare(aNum, undefined, { numeric: true });
  }

  // Assets with numbers come before assets without
  if (aNum && !bNum) return -1;
  if (!aNum && bNum) return 1;

  // If neither has a number, sort by name
  return a.assetName.localeCompare(b.assetName, undefined, { numeric: true });
};

const sortActivitiesByType = (a: ResearchActivity, b: ResearchActivity) => {
  if (a.type === ResearchActivityType.CORE && b.type === ResearchActivityType.SUPPORTING) return -1;
  if (a.type === ResearchActivityType.SUPPORTING && b.type === ResearchActivityType.CORE) return 1;
  return a.sortOrder - b.sortOrder;
};

export const Assets: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [syncResult, setSyncResult] = useState<XeroSyncAssetsResponse | null>(null);

  const [allocationsMap, setAllocationsMap] = useState<Record<string, Record<string, number>>>({});
  const [projectAllocationsMap, setProjectAllocationsMap] = useState<Record<string, number>>({});

  const { organisationId, currentOrganisation } = useAuth();
  const { currentProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();
  const projectId = currentProject?.id;

  // Initialize search filter from URL parameter on mount only
  useEffect(() => {
    const nameParam = searchParams.get('name');
    const assetParam = searchParams.get('asset');

    if (nameParam) {
      setSearchFilter(nameParam);
      searchParams.delete('name');
      setSearchParams(searchParams, { replace: true });
    } else if (assetParam) {
      setSearchFilter(assetParam);
      searchParams.delete('asset');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Debounce search filter
  const debouncedSearchFilter = useDebounce(searchFilter, 300);

  // Load data function
  const loadData = useCallback(async (orgId: string, projId: string) => {
    if (!orgId || !projId) return;

    setLoading(true);
    setError(null);

    try {
      const [assetsResponse, activitiesResponse] = await Promise.all([
        apiClient.get(`/organisations/${orgId}/projects/${projId}/assets?allocatedOnly=false`),
        apiClient.get(`/organisations/${orgId}/projects/${projId}/activities`),
      ]);

      if (!assetsResponse.ok) {
        throw new Error('Failed to fetch assets');
      }

      if (!activitiesResponse.ok) {
        throw new Error('Failed to fetch activities');
      }

      const assetsData = await assetsResponse.json();
      const activitiesData = await activitiesResponse.json();

      if (assetsData.success && activitiesData.success) {
        const newAllocationsMap: Record<string, Record<string, number>> = {};
        const newProjectAllocationsMap: Record<string, number> = {};

        assetsData.assets?.forEach((asset: ProjectAsset) => {
          newProjectAllocationsMap[asset.id] = asset.projectAllocation || 0;
          if (asset.allocations && asset.allocations.length > 0) {
            newAllocationsMap[asset.id] = convertAllocationsToObject(asset.allocations);
          }
        });

        setAssets(assetsData.assets || []);
        setActivities(activitiesData.activities || []);
        setAllocationsMap(newAllocationsMap);
        setProjectAllocationsMap(newProjectAllocationsMap);
      } else {
        throw new Error(assetsData.message || activitiesData.message || 'Failed to load data');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      console.error('Load data error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data effect
  useEffect(() => {
    if (organisationId && projectId) {
      loadData(organisationId, projectId);
    }
  }, [organisationId, projectId, loadData]);

  // Memoized sorted data
  const sortedActivities = useMemo(() => {
    return [...activities].sort(sortActivitiesByType);
  }, [activities]);

  const filteredAssets = useMemo(() => {
    let filtered = assets;
    if (debouncedSearchFilter) {
      const s = debouncedSearchFilter.toLowerCase();
      filtered = assets.filter(
        (a) =>
          a.assetName.toLowerCase().includes(s) ||
          a.assetNumber?.toLowerCase().includes(s) ||
          a.serialNumber?.toLowerCase().includes(s)
      );
    }
    return [...filtered].sort(sortAssetsByNumber);
  }, [assets, debouncedSearchFilter]);

  // Event handlers
  const handleSyncComplete = (result: XeroSyncAssetsResponse) => {
    setSyncResult(result);
    if (result.success && organisationId && projectId) {
      enqueueSnackbar(`Successfully synced ${result.processed} assets`, {
        variant: 'success',
      });
      loadData(organisationId, projectId);
    }
  };

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(event.target.value);
  }, []);

  const clearFilter = useCallback(() => {
    setSearchFilter('');
    searchParams.delete('name');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  // Create allocation rule
  const handleCreateRule = useCallback(
    async (assetId: string, projectAllocation: number, allocations: ActivityAllocation[]) => {
      if (!organisationId || !projectId) return;

      setSaving(assetId);
      try {
        const requestBody = {
          projectAllocation,
          allocations,
        };

        const asset = assets.find((a) => a.id === assetId);
        const hasExistingAllocation =
          (asset?.allocations && asset.allocations.length > 0) ||
          (asset?.projectAllocation && asset.projectAllocation > 0);

        const method = hasExistingAllocation ? 'PUT' : 'POST';
        const response = await apiClient.request(
          `/organisations/${organisationId}/projects/${projectId}/assets/${assetId}/allocate`,
          {
            method,
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to save allocation rule');
        }

        // Update BOTH the main assets state AND the separate maps
        setAssets((prev) =>
          prev.map((asset) =>
            asset.id === assetId
              ? {
                  ...asset,
                  allocations: allocations.map((allocation) => ({
                    id: `temp-${allocation.activityId}-${Date.now()}`,
                    assetId: assetId,
                    projectId: projectId,
                    activityId: allocation.activityId,
                    allocation: allocation.allocation,
                    notes: undefined,
                    allocatedBy: organisationId!,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                  })),
                  projectAllocation: projectAllocation,
                }
              : asset
          )
        );

        // Update the allocationsMap
        setAllocationsMap((prev) => ({
          ...prev,
          [assetId]: convertAllocationsToObject(allocations),
        }));

        // Update the projectAllocationsMap
        setProjectAllocationsMap((prev) => ({
          ...prev,
          [assetId]: projectAllocation,
        }));

        enqueueSnackbar(
          `Asset allocation rule ${hasExistingAllocation ? 'updated' : 'created'} successfully`,
          { variant: 'success' }
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create allocation rule';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, assets, enqueueSnackbar]
  );

  // Delete allocation rule
  const handleDeleteRule = useCallback(
    async (assetId: string) => {
      if (!organisationId || !projectId) return;

      setSaving(assetId);
      try {
        const response = await apiClient.delete(
          `/organisations/${organisationId}/projects/${projectId}/assets/${assetId}/allocate`
        );

        if (!response.ok) {
          throw new Error('Failed to delete allocation rule');
        }

        // Update local state
        setAllocationsMap((prev) => {
          const newMap = { ...prev };
          delete newMap[assetId];
          return newMap;
        });

        setProjectAllocationsMap((prev) => ({
          ...prev,
          [assetId]: 0,
        }));

        setAssets((prev) =>
          prev.map((asset) =>
            asset.id === assetId ? { ...asset, allocations: [], projectAllocation: 0 } : asset
          )
        );

        enqueueSnackbar('Asset allocation rule deleted successfully', { variant: 'info' });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to delete allocation rule';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, enqueueSnackbar]
  );

  // Loading state
  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection
          title="Asset Allocations"
          action={
            <>
              <XeroSyncAssets onSyncComplete={handleSyncComplete} />
              <AssetHelp />
            </>
          }
        >
          <LoadingComponent context="Loading assets and activities..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  // No activities state
  if (activities.length === 0) {
    return (
      <ContentWrapper>
        <PaperSection
          title="Asset Allocations"
          action={
            <>
              <XeroSyncAssets onSyncComplete={handleSyncComplete} />
              <AssetHelp />
            </>
          }
        >
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, my: 10 }}
          >
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No Research Activities"
              message="Create research activities to start setting up asset allocation rules."
              button={
                <Button
                  component={RouterLink}
                  to={`/app/activities/${projectId}`}
                  variant="contained"
                  size="small"
                >
                  Create Your First Activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Asset Allocations - ${currentProject?.name}`}
        action={
          <>
            <XeroSyncAssets onSyncComplete={handleSyncComplete} />
            <AssetHelp />
          </>
        }
      >
        <Box sx={{ mb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Search Filter with Sync Result */}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            {filteredAssets.length > 0 && (
              <Box sx={{ mb: 0, mt: -2, minWidth: 400 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by asset name, number, or serial..."
                  value={searchFilter}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                {debouncedSearchFilter && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Showing {filteredAssets.length} of {assets.length} assets
                  </Typography>
                )}
              </Box>
            )}

            {syncResult?.success && (
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircle color="success" sx={{ fontSize: 16 }} />
                  <Typography variant="body2" color="success.main" fontWeight={500}>
                    Assets synced successfully
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {syncResult.processed} processed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {syncResult.created} created
                  </Typography>
                  {syncResult.updated > 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {syncResult.updated} updated
                      </Typography>
                    </>
                  )}
                  {syncResult.skipped > 0 && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {syncResult.skipped} skipped
                      </Typography>
                    </>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Table */}
        {filteredAssets.length > 0 ? (
          <TableContainer
            component={Box}
            sx={{
              mx: -6,
              px: 6,
              width: 'calc(100% + 96px)',
              minHeight: '60vh',
              position: 'relative',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                WebkitAppearance: 'none',
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, .3)',
              },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 350, minWidth: 350, fontWeight: 600 }}>
                    Assets
                    {assets.length > 0 && <span> ({assets.length} total)</span>}
                  </TableCell>
                  <TableCell
                    sx={{ width: 120, minWidth: 120, textAlign: 'center', fontWeight: 600 }}
                  ></TableCell>
                  <TableCell
                    sx={{
                      width: 120,
                      minWidth: 120,
                      textAlign: 'center',
                      fontWeight: 600,
                      backgroundColor: '#EFF2FE',
                    }}
                  >
                    Project %
                  </TableCell>
                  <TableCell sx={{ width: 60, minWidth: 60, textAlign: 'center', fontWeight: 600 }}>
                    Total
                  </TableCell>
                  {sortedActivities.map((activity) => (
                    <TableCell
                      key={activity.id}
                      sx={{
                        width: 100,
                        minWidth: 100,
                        p: 1,
                        verticalAlign: 'top',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                          size="small"
                          color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          align="center"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {activity.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredAssets.map((asset) => (
                  <AssetTableRow
                    key={asset.id}
                    asset={asset}
                    activities={sortedActivities}
                    allocations={allocationsMap[asset.id] || {}}
                    projectAllocation={projectAllocationsMap[asset.id] || 0}
                    onCreateRule={handleCreateRule}
                    onDeleteRule={handleDeleteRule}
                    isSaving={saving === asset.id}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : debouncedSearchFilter ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No assets found matching "{debouncedSearchFilter}"
            </Typography>
            <Button size="small" onClick={clearFilter} sx={{ mt: 1 }}>
              Clear filter
            </Button>
          </Box>
        ) : (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<DevicesOutlined />}
              title="No assets found"
              message="Avanzu can automatically calculate your asset depreciation and allocate it to your projects. Sync your assets from Xero to get started."
              showBorder={false}
            />
          </Box>
        )}

        {/* Summary */}
        {filteredAssets.length > 0 && (
          <Box
            sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''}
              {Object.values(projectAllocationsMap).filter((allocation) => allocation > 0).length >
                0 && (
                <>
                  {' • '}
                  {
                    Object.values(projectAllocationsMap).filter((allocation) => allocation > 0)
                      .length
                  }
                  {' allocated'}
                </>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activity allocations must total 100% when project allocation is set
            </Typography>
          </Box>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

const AssetHelp: React.FC = memo(() => {
  return (
    <HelpComponent title="Create allocation rules for assets.">
      <Typography variant="body2" paragraph>
        Set project allocation percentages for assets and distribute them across research
        activities. Asset costs (like depreciation) can be claimed as part of R&D tax incentives
        when used for eligible research activities.
      </Typography>
      <Typography variant="body2" paragraph>
        Enter the project allocation percentage (0-100%) and then allocate across activities.
        Activity allocations must total 100% when a project allocation is set.
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DeleteSweep sx={{ fontSize: '16px', color: 'error.main' }} />
          <Typography variant="caption">Remove asset allocation</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <PlaylistAddCheck sx={{ fontSize: '16px', color: 'success.main' }} />
          <Typography variant="caption">Create/update asset allocation</Typography>
        </Stack>
      </Stack>
    </HelpComponent>
  );
});
